// path: src/Achievements.js
import React, { useMemo, useState } from "react";
import "./App.css";
import characters from "./Characters.json";
import {
    buildAchievementContext,
    getAchievementSummary,
} from "./utils/achievements";

const Achievements = ({
    collectionUnlocked = [],
    collectionFoilUnlocked = [],
    storyProgress = null,
    endlessStats = null,
    topScore = 0,
    gamesPlayed = 0,
    totalCards = characters.length,
    title = "Achievements",
    hintText = "",
    embedded = false,
}) => {
    const [openId, setOpenId] = useState(null);
    const [openSections, setOpenSections] = useState(() => ({
        collection: true,
        story: true,
        endless: true,
        volume: true,
    }));
    const summary = useMemo(() => {
        const context = buildAchievementContext({
            collectionUnlocked,
            collectionFoilUnlocked,
            storyProgress,
            endlessStats,
            topScore,
            gamesPlayed,
            totalCards,
        });
        return {
            ...getAchievementSummary(context),
            collectionUnlockedCount: context.unlockedCount,
        };
    }, [
        collectionUnlocked,
        collectionFoilUnlocked,
        storyProgress,
        endlessStats,
        topScore,
        gamesPlayed,
        totalCards,
    ]);

    const toggleAchievement = (id) => {
        setOpenId((current) => (current === id ? null : id));
    };

    const toggleSection = (sectionId) => {
        setOpenSections((current) => ({
            ...current,
            [sectionId]: !current[sectionId],
        }));
    };

    const renderTierChips = (group) => (
        <div className="achievementTierChips" aria-label="Achievement tiers">
            {group.tiers.map((tier) => (
                <span
                    key={tier.id}
                    className={`achievementTierChip${
                        tier.completed ? " achievementTierChip--completed" : ""
                    }`}
                    title={`${tier.tierLabel}: ${tier.title}`}
                >
                    {tier.tierLabel}
                </span>
            ))}
        </div>
    );

    const renderGroupStatus = (group) => {
        if (group.completed) {
            return "Complete";
        }

        if (group.nextTier) {
            return `${group.completedCount}/${group.totalCount}`;
        }

        return "Locked";
    };

    const renderAchievementGroup = (group) => {
        const isOpen = openId === group.id;
        const hasProgress = Boolean(group.nextTier);

        return (
            <div
                className={`achievementItem achievementItem--group ${
                    group.completed
                        ? "achievementItem--completed"
                        : group.completedCount > 0
                          ? "achievementItem--partial"
                          : "achievementItem--locked"
                }${isOpen ? " achievementItem--open" : ""}`}
                role="listitem"
                key={group.id}
            >
                <button
                    type="button"
                    className="achievementItem__toggle"
                    onClick={() => toggleAchievement(group.id)}
                    aria-expanded={isOpen}
                    aria-controls={`achievement-body-${group.id}`}
                >
                    <div className="achievementIcon" aria-hidden="true">
                        🏆
                    </div>
                    <div className="achievementItem__main">
                        <div className="achievementName">{group.groupTitle}</div>
                        {renderTierChips(group)}
                    </div>
                    <div className="achievementStatus">
                        {renderGroupStatus(group)}
                    </div>
                    <span
                        className="achievementItem__chevron"
                        aria-hidden="true"
                    >
                        {isOpen ? "▾" : "▸"}
                    </span>
                </button>

                {isOpen ? (
                    <div
                        className="achievementItem__body"
                        id={`achievement-body-${group.id}`}
                    >
                        {group.groupDescription && (
                            <div className="achievementDescription">
                                {group.groupDescription}
                            </div>
                        )}

                        <div className="achievementTierLegend">
                            {group.tiers.map((tier) => (
                                <div
                                    key={tier.id}
                                    className={`achievementTierLegend__row${
                                        tier.completed
                                            ? " achievementTierLegend__row--completed"
                                            : ""
                                    }`}
                                >
                                    <span className="achievementTierLegend__label">
                                        {tier.tierLabel}
                                    </span>
                                    <span className="achievementTierLegend__title">
                                        {tier.title}
                                    </span>
                                    <span className="achievementTierLegend__target">
                                        {tier.target}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {hasProgress ? (
                            <>
                                <div className="achievementProgressText">
                                    Next tier: {group.progressValue}/
                                    {group.progressTarget}
                                </div>

                                <div
                                    className="achievementProgressBar"
                                    aria-label={`${group.groupTitle} progress`}
                                    aria-valuemin="0"
                                    aria-valuemax={group.progressTarget}
                                    aria-valuenow={group.progressValue}
                                    role="progressbar"
                                >
                                    <div
                                        className="achievementProgressBarFill"
                                        style={{
                                            width: `${group.percent}%`,
                                        }}
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    const renderAchievement = (achievement) => {
        const isOpen = openId === achievement.id;
        return (
            <div
                className={`achievementItem ${
                    achievement.completed
                        ? "achievementItem--completed"
                        : "achievementItem--locked"
                }${
                    achievement.isSecret
                        ? " achievementItem--secret"
                        : ""
                }${isOpen ? " achievementItem--open" : ""}`}
                role="listitem"
                key={achievement.id}
            >
                <button
                    type="button"
                    className="achievementItem__toggle"
                    onClick={() => toggleAchievement(achievement.id)}
                    aria-expanded={isOpen}
                    aria-controls={`achievement-body-${achievement.id}`}
                >
                    <div className="achievementIcon" aria-hidden="true">
                        {achievement.isSecret ? "?" : "🏆"}
                    </div>
                    <div className="achievementItem__main">
                        <div className="achievementName">
                            {achievement.displayTitle}
                        </div>
                    </div>
                    <div className="achievementStatus">
                        {achievement.completed
                            ? "Completed"
                            : achievement.isSecret
                              ? "Hidden"
                              : "Locked"}
                    </div>
                    <span
                        className="achievementItem__chevron"
                        aria-hidden="true"
                    >
                        {isOpen ? "▾" : "▸"}
                    </span>
                </button>

                {isOpen ? (
                    <div
                        className="achievementItem__body"
                        id={`achievement-body-${achievement.id}`}
                    >
                        {achievement.displayDescription && (
                            <div className="achievementDescription">
                                {achievement.displayDescription}
                            </div>
                        )}

                        {!achievement.isSecret && (
                            <>
                                <div className="achievementProgressText">
                                    {achievement.progressValue}/
                                    {achievement.target}
                                </div>

                                <div
                                    className="achievementProgressBar"
                                    aria-label={`${achievement.displayTitle} progress`}
                                    aria-valuemin="0"
                                    aria-valuemax={achievement.target}
                                    aria-valuenow={achievement.progressValue}
                                    role="progressbar"
                                >
                                    <div
                                        className="achievementProgressBarFill"
                                        style={{
                                            width: `${achievement.percent}%`,
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </div>
        );
    };

    const renderDisplayItem = (item) => {
        if (item.type === "group") {
            return renderAchievementGroup(item);
        }

        return renderAchievement(item.achievement);
    };

    return (
        <div
            className={`achievementsScreen${
                embedded ? " achievementsScreen--embedded" : ""
            }`}
        >
            {!embedded ? (
                <div className="achievementsHeader">
                    <h1 className="impactFont pageTitle achievementsTitle">
                        {title}
                    </h1>
                    <div className="achievementsProgress">
                        Unlocked: {summary.completedCount}/{summary.totalCount}
                    </div>
                    <div className="achievementsProgress achievementsProgress--secondary">
                        Collection: {summary.collectionUnlockedCount}/{totalCards}
                    </div>
                </div>
            ) : (
                <div className="achievementsProgress achievementsProgress--embedded">
                    Unlocked: {summary.completedCount}/{summary.totalCount} |
                    Collection: {summary.collectionUnlockedCount}/{totalCards}
                </div>
            )}

            <div className="achievementsSections">
                {(summary.sections || []).map((section) => {
                    const isSectionOpen = openSections[section.id] !== false;
                    return (
                        <section
                            key={section.id}
                            className="achievementsSection"
                            aria-labelledby={`achievement-section-${section.id}`}
                        >
                            <button
                                type="button"
                                className="achievementsSection__toggle"
                                onClick={() => toggleSection(section.id)}
                                aria-expanded={isSectionOpen}
                                id={`achievement-section-${section.id}`}
                            >
                                <span className="achievementsSection__title">
                                    {section.label}
                                </span>
                                <span className="achievementsSection__count">
                                    {section.completedCount}/
                                    {section.totalCount}
                                </span>
                                <span
                                    className="achievementsSection__chevron"
                                    aria-hidden="true"
                                >
                                    {isSectionOpen ? "▾" : "▸"}
                                </span>
                            </button>

                            {isSectionOpen ? (
                                <div
                                    className="achievementsList"
                                    role="list"
                                    aria-label={`${section.label} achievements`}
                                >
                                    {(section.displayItems || []).map(
                                        renderDisplayItem
                                    )}
                                </div>
                            ) : null}
                        </section>
                    );
                })}
            </div>

            {hintText ? (
                <div className="achievementsHint">{hintText}</div>
            ) : null}
        </div>
    );
};

export default Achievements;
